import React, { useEffect, useState } from "react";

import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Tooltip,
  Box,
  Typography,
  Card,
  CardContent,
  Autocomplete,
} from "@mui/material";

import { createFilterOptions, } from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import GetAppIcon from "@mui/icons-material/GetApp";
import AttachmentIcon from "@mui/icons-material/Attachment";
import PublishIcon from "@mui/icons-material/Publish";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PeopleIcon from "@mui/icons-material/People";
import LockIcon from "@mui/icons-material/Lock";

import TableAbstraction from "../../componentUtils/TableAbstraction";

import { useLocalStorage } from "../../utils/LocalStorageHook";
import { checkForRole } from "../../utils/GetAuth";
import * as XLSX from "xlsx"; //not used
import { getAllContracts, saveContract, deleteContract, getContractCustomers, saveContractCustomers, bulkUploadContracts } from "../../utils/GetAdmin";

interface Contract {
  contract_id: number;

  contract_name: string;
  client_id: number;
  sales_org: string;

  contract_start_dt: string;
  contract_end_dt?: string | null;

  contract_renewal_years?: number | null;
  contract_info?: string | null;

  active: boolean;

  customer_count?: number;
}

interface Customer {
  customer_num: string;
  customer_name: string;
  assigned: boolean;
}

export default function AdminContracts() {

  const [salesOrg] = useLocalStorage("SETTINGS.salesOrg", "");
  const [clientId] = useLocalStorage("SETTINGS.clientId", 100);
  const [useCompact] = useLocalStorage("SETTINGS.compact", true);

  const enabled = checkForRole(`${clientId}`, salesOrg, "vp_admin");

  //#region Dialog Alert Message
  const [openAlertDialog, setOpenAlertDialog] = useState<boolean>(false);
  const [alertTitle, setAlertTitle] = useState<string>("");
  const [alertMessage, setAlertMessage] = useState<string>("");

  const showAlert = (title: string, message: string) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setOpenAlertDialog(true);
  };

  const closeAlert = () => {
    setOpenAlertDialog(false);
    setAlertTitle("");
    setAlertMessage("");
  };

  //#endregion


  //List Contract 
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [tableRefreshKey, setTableRefreshKey] = useState(0); //added this to force table refresh
  const [doneLoading, setDoneLoading] = useState<boolean>(false);

  const [openCustomerDialog, setOpenCustomerDialog] = useState<boolean>(false);

  const [customerList, setCustomerList] = useState<Customer[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);

  const [muiTable, setMuiTable] = useState<any>({ rpp: 25, });


  // Selected Contract
  const [selectedContract, setSelectedContract] = useState<Contract | undefined>();
  // Add - Edit Dialog
  const [openContractDialog, setOpenContractDialog] = useState<boolean>(false);
  // Delete Dialog
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  // Contract Form
  const [contractName, setContractName] = useState<string>("");
  const [contractStartDate, setContractStartDate] = useState<string>("");
  const [contractEndDate, setContractEndDate] = useState<string>("");
  const [renewalYears, setRenewalYears] = useState<string>("");
  const [contractInfo, setContractInfo] = useState<string>("");
  const [active, setActive] = useState<boolean>(true);
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  //#region Bulk Contract --Upload
  interface BulkContract {
    contract_name: string;
    contract_start_dt: string;
    contract_end_dt: string;
    customers: string;
    contract_info: string | null;
  }

  const [openBulkDialog, setOpenBulkDialog] = useState(false); //should delete
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkContracts, setBulkContracts] = useState<BulkContract[]>([]);
  const [bulkError, setBulkError] = useState("");
  const [bulkUploading, setBulkUploading] = useState(false);
  const [confirmBulkUpload, setConfirmBulkUpload] = useState(false);

  //#endregion

  // Initial Load
  useEffect(() => {
    if (doneLoading) return;
    getAllContracts(salesOrg).then((v) => {
      if (v && v.data) {
        setContracts(v.data);
      }
      setDoneLoading(true);
    });

  }, [doneLoading, salesOrg]);

  //Resetting Contract Form
  const resetForm = () => {
    setSelectedContract(undefined);
    setContractName("");
    setContractStartDate("");
    setContractEndDate("");
    setRenewalYears("");
    setContractInfo("");
    setActive(true);
  };


  // Add Contract // hidden
  const handleAddContract = () => {
    resetForm();
    setOpenContractDialog(true);
  };


  // Edit Contract
  const handleEditContract = (
    contract: Contract
  ) => {
    setSelectedContract(contract);
    setContractName(contract.contract_name || "");
    setContractStartDate(contract.contract_start_dt || "");
    setContractEndDate(contract.contract_end_dt || "");
    setRenewalYears(contract.contract_renewal_years ? String(contract.contract_renewal_years) : "");
    setContractInfo(contract.contract_info || "");
    setActive(contract.active);
    setOpenContractDialog(true);
  };


  // Close Dialog
  const handleCloseContractDialog = () => {
    setOpenContractDialog(false);
    resetForm();
  };

  const autoCompleteFilterOptions = createFilterOptions<Customer>({
    matchFrom: "any",
    limit: 100,
  });

  // Save Contract
  const handleSaveContract = async () => {
    if (!contractStartDate) {
      showAlert("Validation Error", "Please provide Contract Start Date.");
      return;
    }
    const contractData = {
      contract_id: selectedContract?.contract_id || null,
      contract_name:selectedContract?.contract_name || null,
      contract_start_dt: contractStartDate,
      contract_end_dt: contractEndDate || null,
      contract_renewal_years: renewalYears ? Number(renewalYears) : null,
      contract_info: contractInfo || null,
      active: active,
    };

    const response = await saveContract(salesOrg, contractData);

    if (response && response.data) {
      setContracts(response.data);
      handleCloseContractDialog();
    } else {
      showAlert("Error", "Unable to save contract.");
      return;
    }
  };

  // Delete Contract
  const handleDeleteContract = async () => {
    if (!selectedContract) {
      return;
    }
    const response = await deleteContract(salesOrg, selectedContract.contract_id);

    if (response && response.data) {
      setContracts(response.data);
      setConfirmDelete(false);
      setSelectedContract(undefined);
    } else {
      setConfirmDelete(false);
      showAlert("Error", "Error while deleting the Contract");
      return;
    }
  };

  // Customer Assignment
  const handleAssignCustomers = async (contract: Contract) => {
    setSelectedContract(contract);

    const response = await getContractCustomers(
      salesOrg,
      contract.contract_id
    );

    if (response?.data) {
      const customers: Customer[] = response.data;
      setCustomerList(customers);
      const assignedCustomers = customers.filter((customer) => Boolean(customer.assigned));
      setSelectedCustomers(assignedCustomers);
      setOpenCustomerDialog(true);
    } else {
      showAlert("Error", "Error while loading the Customers");
      return;
    }
  };

  const handleSaveCustomerAssignments = async () => {
    if (!selectedContract) {
      return;
    }

    const customerNums = selectedCustomers.map(
      (customer) => customer.customer_num
    );

    const response = await saveContractCustomers(salesOrg, selectedContract.contract_id, customerNums);

    if (response) {
      const contractResponse = await getAllContracts(salesOrg);
      if (contractResponse?.data) {
        setContracts(contractResponse.data);
      }

      setOpenCustomerDialog(false);
      setCustomerList([]);
      setSelectedCustomers([]);
      setSelectedContract(undefined);
    } else {
      showAlert("Error", "Could not save customer assignments");
      return;
    }
  };

  const formatDate = (value: string | null | undefined) => {
    if (!value) return "";
    return value.split("T")[0];
  };


  const formatExcelDate = (value: unknown): string | null => {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return null;
      return [
        value.getUTCFullYear(),
        String(value.getUTCMonth() + 1).padStart(2, "0"),
        String(value.getUTCDate()).padStart(2, "0"),
      ].join("-");
    }
    const text = String(value).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      return null;
    }
    const date = new Date(`${text}T00:00:00Z`);
    if (
      isNaN(date.getTime()) ||
      date.toISOString().slice(0, 10) !== text
    ) {
      return null;
    }
    return text;
  };

  const handleBulkFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setBulkContracts([]);
    setBulkFileName("");
    setBulkError("");

    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    try {
      if (!/\.csv$/i.test(file.name)) {
        throw new Error("Please select a CSV file.");
      }

      const csvText = await file.text();

      const workbook = XLSX.read(csvText, {
        type: "string",
        raw: true,
      });

      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(
        sheet,
        {
          defval: "",
          raw: true,
        }
      );

      if (rows.length === 0) { throw new Error("Excel file is empty."); }

      if (rows.length > 10000) { throw new Error("Maximum 10,000 contracts allowed."); }

      const requiredColumns = [
        "Contract Name",
        "Contract Start Date",
        "Customers",
      ];
      const headers = Object.keys(rows[0]);

      for (const column of requiredColumns) {
        if (!headers.includes(column)) {
          throw new Error(`Missing column: ${column}`);
        }
      }

      const parsedContracts: BulkContract[] = [];
      const contractKeys = new Set<string>();

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const rowNumber = i + 2;

        const name = String(row["Contract Name"] ?? "").trim();

        const startDate = formatExcelDate(
          row["Contract Start Date"]
        );

        const endValue = row["Contract End Date"];

        const endDate =
          endValue === "" ||
            endValue === null ||
            endValue === undefined
            ? "9999-01-01"
            : formatExcelDate(endValue);

        const customers = String(row["Customers"] ?? "").trim();

        const contractInfo =
          String(row["Contract Info"] ?? "").trim() || null;

        // Required fields
        if (!name || !startDate || !customers) {
          throw new Error(
            `Row ${rowNumber}: Contract Name, Start Date and Customers are required.`
          );
        }

        // Validate End Date
        if (!endDate) {
          throw new Error(
            `Row ${rowNumber}: Invalid Contract End Date.`
          );
        }

        if (endDate < startDate) {
          throw new Error(
            `Row ${rowNumber}: End Date cannot be before Start Date.`
          );
        }

        // Duplicate combination
        const key = JSON.stringify([
          name.toLowerCase(),
          startDate,
          endDate,
          customers.toLowerCase(),
        ]);

        if (contractKeys.has(key)) {
          throw new Error(
            `Row ${rowNumber}: Duplicate contract combination found.`
          );
        }

        contractKeys.add(key);

        parsedContracts.push({
          contract_name: name,
          contract_start_dt: startDate,
          contract_end_dt: endDate,
          customers,
          contract_info: contractInfo,
        });
      }


      setBulkContracts(parsedContracts);
      setBulkFileName(file.name);
    } catch (error) {
      setBulkError(
        error instanceof Error
          ? error.message
          : "Unable to read Excel file."
      );
    }
  };


  const handleBulkUpload = async () => {
    if (bulkContracts.length === 0 || bulkUploading) return;

    setBulkUploading(true);
    setBulkError("");

    try {
      const uploadResponse = await bulkUploadContracts(
        salesOrg,
        bulkContracts
      );

      if (!uploadResponse) {
        throw new Error("Bulk upload failed.");
      }

      // to get updated contracts from database
      const response = await getAllContracts(salesOrg);

      if (!response || !Array.isArray(response.data)) {
        throw new Error("Unable to refresh contract table.");
      }

      // Update table data
      setContracts(response.data);

      // Force TableAbstraction to refresh
      setTableRefreshKey((prev) => prev + 1);

      // Reset bulk upload fields
      setBulkContracts([]);
      setBulkFileName("");
      setConfirmBulkUpload(false);

      showAlert(
        "Success",
        "Contracts uploaded successfully."
      );
    } catch (error: any) {
      setBulkError(
        error.response?.data?.message ||
        error.message ||
        "Bulk contract upload failed."
      );
      setConfirmBulkUpload(false);
    } finally {
      setBulkUploading(false);
    }
  };

  //handleDownloadTemplate
  // const handleDownloadTemplate = () => {
  //   const csvData = [
  //     [
  //       "Contract Name",
  //       "Contract Start Date",
  //       "Contract End Date",
  //       "Customers",
  //       "Contract Info",
  //     ],
  //     [
  //       "School_Contracts",
  //       "2026-01-01",
  //       "2030-12-31",
  //       "0000123456",
  //       "This is Dummy Data",
  //     ],
  //     [
  //       "School_Contracts",
  //       "2026-01-01",
  //       "2030-12-31",
  //       "0000122456",
  //       "This is Dummy Data",
  //     ],
  //     [
  //       "Recreation_Contracts",
  //       "2026-01-01",
  //       "2036-12-31",
  //       "0000123456",
  //       "This is Dummy Data",
  //     ],
  //   ];


  //   const csv = csvData.join(",") + "\r\n";

  //   const blob = new Blob(["\uFEFF" + csv], {
  //     type: "text/csv;charset=utf-8;",
  //   });

  //   const url = window.URL.createObjectURL(blob);
  //   const link = document.createElement("a");

  //   link.href = url;
  //   link.download = "Contract_Upload_Template.csv";

  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);

  //   window.URL.revokeObjectURL(url);
  // };


  const handleDownloadTemplate = () => {
    const csvContent = [
      [
        "Contract Name",
        "Contract Start Date",
        "Contract End Date",
        "Contract Info",
        "Customers",
      ],
      [
        "Sports venue Contract",
        "2026-10-27",
        "2027-10-27",
        "This is a DUMMY Data",
        "0502275452",
      ],
      [
        "Sports venue Contract",
        "2026-10-27",
        "2027-10-27",
        "This is a DUMMY Data",
        "0500666666",
      ],
      [
        "Sports venue Contract",
        "2026-10-27",
        "2027-10-27",
        "This is a DUMMY Data",
        "05006644444",
      ],
      [
        "Sports venue Contract",
        "2026-10-27",
        "2027-10-27",
        "This is a DUMMY Data",
        "050077323065",
      ],
      [
        "Sports venue Contract",
        "2026-10-27",
        "2027-10-27",
        "This is a DUMMY Contract-Sports",
        "4344343",
      ],
    ]
      .map((row) =>
        row.map((value) => `"${value.replace(/"/g, '""')}"`).join(",")
      )
      .join("\r\n");

    const blob = new Blob(["\uFEFF", csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "Bulk_Contracts_Template.csv";
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };


  return (
    <div>
      {doneLoading && enabled ? (
        <>
          {/* BULK CONTRACT UPLOAD */}

          {/**/}{/* BULK CONTRACT UPLOAD */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Upload Bulk: Contracts
                </Typography>

                <Typography
                  variant="body2"
                  color="error"
                  sx={{ mt: 1 }}
                >
                  Please note: Uploading a new bulk contract file will permanently
                  delete all existing contract records and replace them with the
                  records from the uploaded file. This action cannot be undone.
                </Typography>
              </Box>

              <Grid container spacing={2} alignItems="center">
                {/* Get Template */}
                <Grid item>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={<GetAppIcon />}
                    onClick={handleDownloadTemplate}
                  >
                    Get Template
                  </Button>
                </Grid>

                {/* Select File */}
                <Grid item>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    component="label"
                    startIcon={<AttachmentIcon />}
                    disabled={bulkUploading}
                  >
                    Select File
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      hidden
                      onChange={handleBulkFileChange}
                    />
                  </Button>
                </Grid>

                {/* Selected File Name */}
                <Grid item xs={12} sm>
                  <TextField
                    variant="standard"
                    size="small"
                    placeholder="No file selected"
                    value={bulkFileName}
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                </Grid>

                {/* Upload */}
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<PublishIcon />}
                    disabled={
                      bulkContracts.length === 0 || bulkUploading
                    }
                    onClick={() => setConfirmBulkUpload(true)}
                  >
                    Upload
                  </Button>
                </Grid>
              </Grid>

              {/* Validation Success */}
              {bulkContracts.length > 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {bulkContracts.length} contracts validated.
                </Alert>
              )}

              {/* Validation Error */}
              {bulkError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {bulkError}
                </Alert>
              )}
            </CardContent>
          </Card>


          <TableAbstraction key={tableRefreshKey} settingsKey="ADM_CONTRACT" title="Contract Configurations"
            data={contracts}
            options={{
              draggableColumns: { enabled: true, },
              rowsPerPageOptions: [25, 50, 100,],
              serverSide: false,
              selectableRows: "none",
              rowsPerPage: muiTable.rpp,

              onChangeRowsPerPage:
                (numberOfRows) => {
                  muiTable.rpp = numberOfRows;
                  setMuiTable({
                    ...muiTable,
                  });
                },
              // customToolbar: () => {
              //   return (
              //     <Tooltip title="Add Contract">
              //       <IconButton onClick={handleAddContract}>
              //         <AddIcon />
              //       </IconButton>
              //     </Tooltip>
              //   );
              // },
            }}
            columns={[
              {
                label: "Contract Name",
                name: "contract_name",
                options: {
                  searchable: true,
                  filter: true,
                },
              },
              {
                label: "Contract Start Date",
                name: "contract_start_dt",
                options: {
                  customBodyRender: (value: any) => formatDate(value),
                },
              },
              {
                label: "Contract End Date",
                name: "contract_end_dt",
                options: {
                  customBodyRender: (value: any) => formatDate(value),
                },
              },
              {
                label: "Contract Info",
                name: "contract_info",
                options: {
                  searchable: true,
                  filter: false,
                },
              },
              {
                label: "# Customers",
                name: "customer_count",
                options: {
                  filter: false,
                  customBodyRender: (value: number) => value ?? 0,
                },
              },
              {
                label: " ",
                name: "actions",
                options: {
                  filter: false,
                  setCellProps: () => ({ align: "right", }),
                  renderCell: (props) => {
                    const row = props.row as Contract;

                    if (useCompact) {
                      return (
                        <React.Fragment>
                          <Tooltip title="Assign Customers">
                            <PeopleIcon
                              fontSize="small"
                              style={{ marginRight: 10, cursor: "pointer", }}
                              onClick={() => handleAssignCustomers(row)} />
                          </Tooltip>


                          <Tooltip title="Edit" >
                            <EditIcon fontSize="small" style={{
                              marginRight: 10,
                              cursor: "pointer",
                            }}
                              onClick={() => handleEditContract(row)} />
                          </Tooltip>

                          <Tooltip title="Delete">
                            <DeleteIcon fontSize="small" style={{ cursor: "pointer", }}
                              onClick={() => {
                                setSelectedContract(row);
                                setConfirmDelete(true);
                              }} />
                          </Tooltip>
                        </React.Fragment>
                      );
                    }

                    return (
                      <React.Fragment>
                        <Tooltip title="Assign Customers" >
                          <IconButton onClick={() => handleAssignCustomers(row)}>
                            <PeopleIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit" >
                          <IconButton onClick={() => handleEditContract(row)} >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete"  >
                          <IconButton
                            onClick={() => {
                              setSelectedContract(row);
                              setConfirmDelete(true);
                            }} >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </React.Fragment>
                    );
                  },
                },
              },
            ]} />
        </>
      ) : enabled ? (
        <div style={{ textAlign: "center", }}>
          <CircularProgress color="primary" />
        </div>
      ) : (

        <Alert severity="error">
          <AlertTitle>
            <LockIcon />
            Access Error
          </AlertTitle>
          You do not currently have access to <strong>Admin</strong> activities for the{" "} <strong>  {salesOrg} </strong>{" "}
          bottler.
        </Alert>

      )}
      {/* ADD or EDIT CONTRACT */}

      <Dialog
        open={openContractDialog}
        onClose={handleCloseContractDialog}
        fullWidth
        maxWidth="md" >
        <DialogTitle>
          {selectedContract ? "Edit Contract" : "Add Contract"}
        </DialogTitle>


        <DialogContent>

          <Grid container spacing={3} sx={{ marginTop: 0, }} >
            {selectedContract && (
              <Grid item xs={12} md={12}>
                <TextField
                  label="Contract Name" 
                  fullWidth
                  disabled
                  value={selectedContract.contract_name} 
                />
              </Grid>
            )}


            {/* Sales Org */}
            {/* <Grid item xs={12} md={6} >
              <TextField
                label="Sales Org"
                fullWidth
                disabled
                value={salesOrg} />
            </Grid> */}

            {/* Contract Name */}
            {/* <Grid item xs={12} md={6} >
              <TextField
                label="Contract Name"
                type="text"
                required
                fullWidth
                disabled // Remove this line if you want the user to be able to type/edit
                inputProps={{ inputLabel: { shrink: true } }} // Updated syntax for modern Material-UI (v7+)
                value={contractName} // Changed from contractStartDate
                onChange={(e) => setContractName(e.target.value)}
              />

            </Grid> */}

            {/* Start Date */}
            <Grid item xs={12} md={6} >
              <TextField
                label="Contract Start Date"
                type="date"
                required
                fullWidth
                InputLabelProps={{ shrink: true, }}
                value={contractStartDate}
                onChange={(e) => setContractStartDate(e.target.value)} />
            </Grid>


            {/* End Date */}

            <Grid item xs={12} md={6}  >
              <TextField
                label="Contract End Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true, }}
                value={contractEndDate}
                onChange={(e) => setContractEndDate(e.target.value)} />
            </Grid>


            {/* Renewal Years */}

            {/* <Grid item xs={12} md={6} >
              <TextField
                label="Contract Renewal Years"
                type="number"
                fullWidth
                value={renewalYears}
                inputProps={{ min: 0, }}
                onChange={(e) => setRenewalYears(e.target.value)} />
            </Grid> */}


            {/* Active */}

            <Grid item xs={12} md={6} >
              <FormControlLabel
                control={
                  <Switch
                    checked={active} disabled
                    onChange={(e) => setActive(e.target.checked)} />} label="Active" />
            </Grid>

            {/* Contract Info */}
            <Grid item xs={12} >
              <TextField
                label="Contract Information"
                fullWidth
                multiline
                minRows={4}
                value={contractInfo}
                onChange={(e) => setContractInfo(e.target.value)}
                inputProps={{ maxLength: 1000, }} />
            </Grid>
          </Grid>
        </DialogContent>


        <DialogActions>
          <Button onClick={handleCloseContractDialog} >  Cancel </Button>
          <Button variant="contained" onClick={handleSaveContract} > Save </Button>
        </DialogActions>
      </Dialog>

      {/* ASSIGN CUSTOMERS */}

      <Dialog
        open={openCustomerDialog}
        onClose={() => {
          setOpenCustomerDialog(false);
          setSelectedContract(undefined);
          setCustomerList([]);
          setSelectedCustomers([]);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Assign Customers
        </DialogTitle>

        <DialogContent>
          {selectedContract && (
            <Typography
              variant="subtitle1"
              sx={{ marginBottom: 2 }}
            >
              Contract Name:{" "}
              <strong>
                {selectedContract.contract_name}
              </strong>
            </Typography>
          )}

          <Autocomplete<Customer, true, false, false>
            multiple
            disableCloseOnSelect
            options={customerList}
            value={selectedCustomers}
            filterOptions={autoCompleteFilterOptions}
            renderTags={() => null}

            getOptionLabel={(option) =>
              `${option.customer_num} - ${option.customer_name}`
            }

            isOptionEqualToValue={(option, value) =>
              option.customer_num === value.customer_num
            }

            onChange={(_, newValue) => {
              setSelectedCustomers(newValue);
            }}

            renderOption={(props, option, { selected }) => {
              return (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    checked={selected}
                    sx={{ marginRight: 1 }}
                  />

                  {option.customer_num} - {option.customer_name}
                </li>
              );
            }}

            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Customers"
                placeholder="Customer Number or Name"
                helperText="Select customers assigned to this contract"
              />
            )}

            sx={{ marginTop: 1 }}
          />

          <Typography
            variant="subtitle2"
            sx={{ marginTop: 3, marginBottom: 1 }}
          >
            Selected Customers ({selectedCustomers.length})
          </Typography>

          <List
            dense
            sx={{
              maxHeight: 250,
              overflow: "auto",
              border: "1px solid #ddd",
              borderRadius: 1,
            }}
          >
            {selectedCustomers.map((customer) => (
              <ListItem
                key={customer.customer_num}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => {
                      setSelectedCustomers((current) =>
                        current.filter(
                          (item) =>
                            item.customer_num !== customer.customer_num
                        )
                      );
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={`${customer.customer_num} - ${customer.customer_name}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpenCustomerDialog(false);
              setSelectedContract(undefined);
              setCustomerList([]);
              setSelectedCustomers([]);
            }}
          > Cancel </Button>

          <Button
            variant="contained"
            onClick={handleSaveCustomerAssignments}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* BULK UPLOAD CONFIRMATION */}
      <Dialog open={confirmBulkUpload} onClose={() => {
        if (!bulkUploading) setConfirmBulkUpload(false);
      }}>

        <DialogTitle>Confirm Bulk Contract Upload</DialogTitle>

        <DialogContent>
          <DialogContentText>
            This action will delete all existing contracts and
            customer assignments for Sales Org
            <strong> {salesOrg}</strong> and insert
            <strong> {bulkContracts.length} new contracts</strong>.
            <br />
            <br />
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setConfirmBulkUpload(false)}
            disabled={bulkUploading}>
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleBulkUpload}
            disabled={bulkUploading}
          >
            {bulkUploading ? "Uploading..." : "Confirm Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION */}
      <Dialog
        open={confirmDelete}
        onClose={() =>
          setConfirmDelete(false)
        } >
        <DialogTitle> Confirm Deletion </DialogTitle>


        <DialogContent>
          <DialogContentText> Are you sure you wish to delete  Contract{" "}
            <strong> {selectedContract?.contract_name}</strong>
            ?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDeleteContract} color="primary">  Yes </Button>
          <Button onClick={() => { setSelectedContract(undefined); setConfirmDelete(false); }}
            color="primary"
            autoFocus> No </Button>
        </DialogActions>
      </Dialog>

      {/* ALERT DIALOG Common should be used where all we nned to show the alert Dialog*/}
      <Dialog open={openAlertDialog} onClose={closeAlert} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">
          {alertTitle}
        </DialogTitle>

        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {alertMessage}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeAlert} color="primary" autoFocus> OK </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}